import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { Wordmark } from "./Logo";
import { TopBar } from "./TopBar";
import { CATEGORIES, SERVICES } from "@/data/services";
import { NAV } from "@/data/nav";
import { usePortfolioCount } from "@/lib/portfolio";

const GRID_BG = "linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px)";
const ext = (to) => /^(https?:|mailto:)/.test(to);

/* Shared dropdown chrome: gold rail, drafting-grid texture, slide-down reveal. No inner scroll. */
const DropPanel = ({ children, width = "w-[560px]", align = "center", testid }) => (
  <div data-testid={testid} className={`invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-[opacity,transform,visibility] duration-300 ease-out absolute top-full pt-4 z-50 ${align === "center" ? "left-1/2 -translate-x-1/2" : align === "left" ? "left-0" : "right-0"}`}>
    <div className={`${width} max-w-[calc(100vw-40px)] relative overflow-hidden border border-white/10 bg-[#081828] shadow-[0_30px_80px_-20px_rgba(0,0,0,.8)]`} style={{ backgroundImage: GRID_BG, backgroundSize: "28px 28px" }}>
      <span aria-hidden className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-[#D8B66A] via-[#D8B66A]/60 to-transparent" />
      {children}
    </div>
  </div>
);

const DropLink = ({ to, children, testid, hint }) => {
  const cls = "group/l flex items-start gap-3 py-2 pr-2 text-[13px] leading-snug text-[#c3d0dd] transition-colors hover:text-white";
  const inner = <><span className="mt-[7px] h-px w-3 shrink-0 bg-[#D8B66A]/50 transition-all duration-300 group-hover/l:w-5 group-hover/l:bg-[#D8B66A]" /><span>{children}{hint && <span className="mt-0.5 block text-[11px] text-[#7f94aa]">{hint}</span>}</span></>;
  return ext(to) ? <a href={to} target={to.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid} className={cls}>{inner}</a> : <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
};

const PanelHead = ({ eyebrow, title, to, cta }) => (
  <div className="flex items-end justify-between gap-6 border-b border-white/10 px-6 pb-4 pt-6">
    <div><p className="eyebrow text-[10px] text-[#D8B66A]">{eyebrow}</p><p className="mt-1 font-display text-xl font-bold text-white">{title}</p></div>
    {to && <Link to={to} className="u-link whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">{cta}</Link>}
  </div>
);

/* Generic two-column panel driven by data/nav.js — every link shows its focused detail line. */
const ConfigPanel = ({ item }) => (
  <DropPanel width={item.width} align={item.align} testid={`drop-${item.id}-panel`}>
    <PanelHead eyebrow={item.panel.eyebrow} title={item.panel.title} to={item.panel.cta[1]} cta={item.panel.cta[0]} />
    <div className="grid grid-cols-2 gap-x-8 px-6 py-5">
      {item.panel.cols.map((col) => (
        <div key={col.title}><p className="eyebrow mb-2 text-[10px] text-[#9fb3c8]">{col.title}</p>{col.links.map((l) => <DropLink key={l.id} to={l.to} hint={l.hint} testid={`drop-${l.id}`}>{l.label}</DropLink>)}</div>
      ))}
    </div>
  </DropPanel>
);

/* Services: two-pane mega menu. Hover a category on the left, its services render on the right. Everything fits without scrolling. */
const ServicesMega = () => {
  const [active, setActive] = useState(CATEGORIES[0].id);
  const cat = CATEGORIES.find((c) => c.id === active);
  const list = SERVICES.filter((s) => s.cat === active);
  return (
    <DropPanel width="w-[920px] 2xl:w-[960px]" align="left" testid="mega-services-panel">
      <div className="grid grid-cols-[300px_1fr]">
        <aside className="border-r border-white/10 bg-white/[.025] py-4">
          <p className="eyebrow px-6 pb-3 text-[10px] text-[#D8B66A]">Seven categories · {SERVICES.length} services</p>
          {CATEGORIES.map((c) => {
            const n = SERVICES.filter((s) => s.cat === c.id).length;
            const on = c.id === active;
            return (
              <Link key={c.id} to={`/services?cat=${c.id}`} data-testid={`mega-cat-${c.id}`} onMouseEnter={() => setActive(c.id)} onFocus={() => setActive(c.id)}
                className={`relative flex items-center gap-4 px-6 py-[11px] text-left transition-colors ${on ? "bg-[#0A1D30] text-white" : "text-[#9fb3c8] hover:text-white"}`}>
                <span className={`absolute inset-y-0 left-0 w-[3px] transition-transform duration-300 origin-top ${on ? "scale-y-100 bg-[#D8B66A]" : "scale-y-0 bg-[#D8B66A]"}`} />
                <span className={`font-mono text-[12px] font-bold ${on ? "text-[#D8B66A]" : "text-[#6f8499]"}`}>{c.code}</span>
                <span className="flex-1 text-[13px] font-semibold">{c.label}</span>
                <span className="font-mono text-[11px] text-[#6f8499]">{String(n).padStart(2, "0")}</span>
              </Link>
            );
          })}
        </aside>
        <div className="flex flex-col">
          <div className="flex items-end justify-between gap-6 border-b border-white/10 px-7 pb-4 pt-6">
            <div className="flex items-baseline gap-4">
              <span className="font-display text-4xl font-bold leading-none text-[#D8B66A]/90">{cat.code}</span>
              <div><p className="eyebrow text-[10px] text-[#9fb3c8]">Category {cat.code} of G</p><p className="font-display text-xl font-bold text-white">{cat.label}</p></div>
            </div>
            <Link to={`/services?cat=${cat.id}`} data-testid="mega-cat-view" className="u-link whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">View category →</Link>
          </div>
          <AnimatePresence mode="wait">
            <motion.ul key={active} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.18 }} className="grid flex-1 grid-cols-2 gap-x-8 px-7 py-5 content-start" data-testid="mega-service-list">
              {list.map((s) => <li key={s.slug}><DropLink to={`/services/${s.slug}`} testid={`mega-service-${s.slug}`}>{s.title}</DropLink></li>)}
            </motion.ul>
          </AnimatePresence>
          <Link to="/services" data-testid="mega-all-services" className="group/a flex items-center justify-between border-t border-white/10 bg-white/[.03] px-7 py-4 text-[12px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#1769AA]">
            Browse all {SERVICES.length} services <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/a:translate-x-1" />
          </Link>
        </div>
      </div>
    </DropPanel>
  );
};

const mobileSubs = (item) => (item.mega ? [...CATEGORIES.map((c) => ({ id: `cat-${c.id}`, label: `${c.code}. ${c.label}`, to: `/services?cat=${c.id}` })), { id: "all", label: `View all ${SERVICES.length} services →`, to: "/services" }] : item.panel.cols.flatMap((c) => c.links));

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState(null);
  const { pathname } = useLocation();
  const portfolioCount = usePortfolioCount();
  const nav = portfolioCount > 0 ? NAV.map((n) => (n.id === "about" ? { ...n, panel: { ...n.panel, cols: [n.panel.cols[0], { ...n.panel.cols[1], links: [...n.panel.cols[1].links, { id: "about-portfolio", label: "Portfolio", to: "/portfolio", hint: "Approved client project sheets — published with permission." }] }] } } : n)) : NAV;
  useEffect(() => { setOpen(false); setSub(null); }, [pathname]);
  const isOn = (item) => (item.to === "/" ? pathname === "/" : item.to.startsWith("/#") ? false : pathname.startsWith(item.to.split("#")[0]));

  return (
    <header data-testid="site-header" className="on-dark fixed top-0 inset-x-0 z-50 bg-[#0A1D30]/95 backdrop-blur-xl border-b border-white/10">
      <TopBar hidden={false} />
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4 xl:gap-6">
        <Link to="/" data-testid="header-logo-link" aria-label="GEO DRAFTING home" className="shrink-0"><Wordmark /></Link>
        <nav className="hidden xl:flex flex-1 items-center justify-evenly gap-2 2xl:gap-3 min-w-0" aria-label="Main">
          {nav.map((item) => {
            const Icon = item.icon;
            const hashOnly = item.to.startsWith("/#"); // anchor into the home page: never "current page"
            const Comp = hashOnly ? Link : NavLink;
            const cls = `u-link flex items-center gap-1 whitespace-nowrap text-[10.5px] 2xl:text-[11px] font-semibold tracking-[0.04em] 2xl:tracking-[0.06em] uppercase text-white/85 ${isOn(item) ? "active text-white" : ""}`;
            return (
              <div key={item.id} className="relative group" data-testid={`nav-drop-${item.id}`}>
                <Comp to={item.to} {...(hashOnly ? { className: cls } : { end: item.to === "/", className: () => cls })} data-testid={`nav-${item.id}-link`} aria-haspopup="true">
                  <Icon className="hidden 2xl:block h-3.5 w-3.5 shrink-0 text-[#D8B66A]/90 transition-colors group-hover:text-[#D8B66A]" aria-hidden />
                  {item.label}<ChevronDown className="h-3 w-3 shrink-0 opacity-70 transition-transform duration-300 group-hover:rotate-180" />
                </Comp>
                {item.mega ? <ServicesMega /> : <ConfigPanel item={item} />}
              </div>
            );
          })}
        </nav>
        <div className="flex shrink-0 items-center gap-3">
          <Link to="/get-started" data-testid="header-get-started-button" className="btn btn-primary hidden sm:inline-flex !py-2.5 !px-4 md:!px-5 whitespace-nowrap text-[11px] md:text-[13px]">
            Get Started <ArrowRight className="arr h-4 w-4" />
          </Link>
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-expanded={open} aria-label="Toggle menu" className="xl:hidden h-11 w-11 grid place-items-center border border-white/20 text-white">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} data-lenis-prevent className="xl:hidden border-t border-white/10 bg-[#0A1D30] max-h-[80vh] overflow-y-auto" aria-label="Mobile">
            <div className="px-5 py-6 flex flex-col gap-1">
              {nav.map((item, i) => {
                const Icon = item.icon;
                const key = item.id;
                return (
                  <div key={key} className="border-b border-white/5 py-3">
                    <div className="flex items-center justify-between gap-4">
                      <Link to={item.to} data-testid={`mobile-nav-${key}-link`} aria-current={isOn(item) ? "page" : undefined} className={`flex items-center gap-4 font-display text-2xl font-bold ${isOn(item) ? "text-[#D8B66A]" : "text-white"}`}>
                        <span className="font-mono text-xs text-[#9fb3c8]">0{i + 1}</span><Icon className="h-5 w-5 text-[#D8B66A]" aria-hidden />{item.label}
                      </Link>
                      <button type="button" onClick={() => setSub((s) => (s === key ? null : key))} aria-expanded={sub === key} data-testid={`mobile-sub-${key}-toggle`} aria-label={`Show ${item.label} subpages`} className="h-9 w-9 grid place-items-center border border-white/20 text-white">
                        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${sub === key ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    <AnimatePresence>
                      {sub === key && (
                        <motion.ul initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden pl-10 pt-2">
                          {mobileSubs(item).map((l) => (
                            <li key={l.id}>
                              {ext(l.to) ? <a href={l.to} data-testid={`mobile-sub-${key}-${l.id}`} className="u-link block py-1.5 text-[13px] text-[#c3d0dd]">{l.label}</a> : <Link to={l.to} data-testid={`mobile-sub-${key}-${l.id}`} className="u-link block py-1.5 text-[13px] text-[#c3d0dd]">{l.label}</Link>}
                              {l.hint && <span className="block pb-1 text-[11px] text-[#7f94aa]">{l.hint}</span>}
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
              <Link to="/get-started" data-testid="mobile-get-started-button" className="btn btn-primary mt-5 justify-center">Get Started <ArrowRight className="arr h-4 w-4" /></Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
